import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Paper,
    Switch,
    Typography
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
    DataGrid,
    GridColDef,
    GridRenderCellParams
} from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AIService } from '../../types';
import {
    createAIService,
    deleteAIService,
    fetchAIServices,
    updateAIService
} from '../../services/api';
import { dataGridStyles } from '../../styles/dataGridStyles';
import AIServiceDialog from './AIServiceDialog';

const AIServiceManagementPanel: React.FC = () => {
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [editingService, setEditingService] = useState<AIService | undefined>(undefined);
    const [showActiveOnly, setShowActiveOnly] = useState(true);

    const queryClient = useQueryClient();

    // Query for fetching AI services
    const { data: allServices = [], isLoading, error } = useQuery<AIService[]>({
        queryKey: ['aiServices'],
        queryFn: () => fetchAIServices(undefined),
    });

    // ✅ НОВОЕ: Фильтрация сервисов в зависимости от состояния фильтра
    const services = React.useMemo(() => {
        return showActiveOnly
            ? allServices.filter(service => service.is_active)
            : allServices;
    }, [allServices, showActiveOnly]);

    // Mutations for CRUD operations
    const createMutation = useMutation({
        mutationFn: createAIService,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['aiServices'] });
            setDialogOpen(false);
            setEditingService(undefined);
        },
        onError: (error) => {
            console.error('Failed to create AI service:', error);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: Partial<AIService> }) =>
            updateAIService(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['aiServices'] });
            setDialogOpen(false);
            setEditingService(undefined);
        },
        onError: (error) => {
            console.error('Failed to update AI service:', error);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteAIService,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['aiServices'] });
        },
        onError: (error) => {
            console.error('Failed to delete AI service:', error);
        },
    });

    const handleSave = async (serviceData: Partial<AIService>): Promise<void> => {
        if (editingService) {
            updateMutation.mutate({
                id: editingService.id,
                data: serviceData
            });
        } else {
            createMutation.mutate(serviceData);
        }
    };

    const handleEdit = (service: AIService) => {
        setEditingService(service);
        setDialogOpen(true);
    };

    const handleDelete = (id: number) => {
        if (window.confirm('Are you sure you want to delete this AI service?')) {
            deleteMutation.mutate(id);
        }
    };

    const handleCreate = () => {
        setEditingService(undefined);
        setDialogOpen(true);
    };

    // ✅ НОВОЕ: Обработчик переключения фильтра
    const handleFilterToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowActiveOnly(event.target.checked);
    };

    // Колонки с исправленным типом и renderCell для действий
    const columns: GridColDef<AIService>[] = [
        {
            field: 'name',
            headerName: 'Name',
            width: 200,
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'code',
            headerName: 'Code',
            width: 150,
        },
        {
            field: 'provider',
            headerName: 'Provider',
            width: 120,
            renderCell: (params: GridRenderCellParams) => (
                <Chip label={params.value} size="small" color="primary" variant="outlined" />
            ),
        },
        {
            field: 'supported_tasks',
            headerName: 'Supported Tasks',
            width: 300,
            flex: 1,
            minWidth: 250,
            renderCell: (params: GridRenderCellParams) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 1 }}>
                    {Array.isArray(params.value) &&
                        params.value.slice(0, 3).map((task: string) => (
                            <Chip key={task} label={task} size="small" />
                        ))}
                    {Array.isArray(params.value) && params.value.length > 3 && (
                        <Chip label={`+${params.value.length - 3} more`} size="small" variant="outlined" />
                    )}
                </Box>
            ),
        },
        {
            field: 'is_active',
            headerName: 'Active',
            width: 80,
            type: 'boolean',
            editable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value ? 'Active' : 'Inactive'}
                    size="small"
                    color={params.value ? 'success' : 'default'}
                />
            ),
        },
        {
            field: 'access_level',
            headerName: 'Access Level',
            width: 120,
            renderCell: (params: GridRenderCellParams) => (
                <Chip label={params.value} size="small" color="secondary" variant="outlined" />
            ),
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams<AIService>) => (
                <Box>
                    <IconButton
                        aria-label="edit"
                        size="small"
                        onClick={() => handleEdit(params.row)}
                        color="primary"
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        aria-label="delete"
                        size="small"
                        onClick={() => handleDelete(params.row.id)}
                        color="error"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            ),
        },
    ];

    if (isLoading) {
        return (
            <Paper sx={{ p: 3 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size={48} />
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            Loading AI services...
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Failed to load AI services
                    </Typography>
                    <Typography variant="body2">{(error as Error).message}</Typography>
                </Alert>
                <Button variant="contained" onClick={() => window.location.reload()}>
                    Retry
                </Button>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h5" component="h2" gutterBottom>
                        AI Services Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configure and manage AI service integrations, system settings, and pricing models
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                    disabled={createMutation.isLoading}
                >
                    {createMutation.isLoading ? 'Creating...' : 'Add AI Service'}
                </Button>
            </Box>

            {/* ✅ НОВОЕ: Панель с фильтрами и статистикой */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    p: 2,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showActiveOnly}
                                onChange={handleFilterToggle}
                                color="primary"
                                size="small"
                            />
                        }
                        label={
                            <Typography variant="body2">
                                Show active only
                            </Typography>
                        }
                        sx={{ m: 0 }}
                    />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing
                        </Typography>
                        <Typography variant="h6" color="primary">
                            {services.length}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Active
                        </Typography>
                        <Typography variant="h6" color="success.main">
                            {allServices.filter(s => s.is_active).length}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Total
                        </Typography>
                        <Typography variant="h6" color="text.primary">
                            {allServices.length}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {services.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {showActiveOnly
                            ? 'No active AI services found'
                            : 'No AI services configured'
                        }
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {showActiveOnly
                            ? 'Try switching off the "Active only" filter or activate some services'
                            : 'Start by adding your first AI service integration'
                        }
                    </Typography>
                    {showActiveOnly && allServices.length > 0 ? (
                        <Button
                            variant="outlined"
                            onClick={() => setShowActiveOnly(false)}
                            size="large"
                        >
                            Show All Services
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreate}
                            size="large"
                        >
                            Add First AI Service
                        </Button>
                    )}
                </Box>
            ) : (
                <Box sx={{ height: 600, width: '100%' }}>
                    <DataGrid
                        rows={services}
                        columns={columns}
                        rowsPerPageOptions={[10, 25, 50]}
                        initialState={{
                            pagination: { page: 0, pageSize: 10 },
                            sorting: { sortModel: [{ field: 'sort_order', sort: 'asc' }] },
                        }}
                        checkboxSelection
                        disableSelectionOnClick
                        loading={deleteMutation.isLoading}
                        getRowHeight={() => 'auto'}
                        sx={{
                            ...dataGridStyles,
                            '& .MuiDataGrid-cell': {
                                borderColor: 'rgba(255, 255, 255, 0.1)',
                                color: 'text.primary',
                                display: 'flex',
                                alignItems: 'center',
                            },
                        }}
                    />
                </Box>
            )}

            <AIServiceDialog
                open={isDialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                    setEditingService(undefined);
                }}
                aiService={editingService}
                onSave={handleSave}
            />

            {/* Loading states */}
            {(createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading) && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: 1000,
                    }}
                >
                    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">
                            {createMutation.isLoading && 'Creating service...'}
                            {updateMutation.isLoading && 'Updating service...'}
                            {deleteMutation.isLoading && 'Deleting service...'}
                        </Typography>
                    </Paper>
                </Box>
            )}
        </Paper>
    );
};

export default AIServiceManagementPanel;
